import { ErroComChave } from "./types";
import type { Command, ErroDaSessao, Reply } from "./types";
import { criarEspelho, type Espelho } from "./saves/espelho";
import { registrarAviso } from "../erros";

/**
 * Comandos depois dos quais o worker gravou alguma coisa no IndexedDB.
 *
 * Três deles salvam sozinhos, sem o jogador pedir, e é justamente por isso que
 * a lista precisa estar certa: o espelho que perde uma rodada guarda um save
 * velho, e um save velho passa a impressão de estar protegido sem estar.
 */
const GRAVAM = new Set<Command["type"]>([
  "pick",
  "advance",
  "liveFinish",
  "save",
  "buyNow",
  "venderAgora",
  "promoverGaroto",
  "venderGaroto",
  "dispensarGaroto",
  "trocarTecnicoDaBase",
  "importarSave",
]);

/**
 * A sessão acabou e alguém ainda está falando com ela.
 *
 * Como tudo que esta pasta devolve, é chave e não frase: quem escreve é a tela,
 * no idioma de quem está lendo.
 */
function encerrada(): ErroComChave {
  return new ErroComChave("sessao.encerrada");
}

/**
 * Ponte entre a interface e o motor.
 *
 * Um Web Worker por sessão, com correlação por id. A interface nunca toca no
 * mundo: pede comandos e recebe projeções, exatamente como vai falar com o
 * servidor no modo online.
 *
 * Aqui também mora o espelho durável dos saves, e não no worker. Os plugins de
 * SQLite do Capacitor e do Tauri só alcançam o nativo por `window`, que num
 * worker não existe, então a cópia tem que sair da thread da interface.
 */
export class GameRunner {
  private worker: Worker;
  private seq = 0;
  private disposed = false;
  private pending = new Map<number, { resolve: (reply: Reply) => void; reject: (error: Error) => void }>();
  private espelho: Espelho = criarEspelho({ registrar: registrarAviso });

  constructor() {
    this.worker = new Worker(new URL("./engine.worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (
      event: MessageEvent<{ id: number; ok: boolean; reply?: Reply; error?: ErroDaSessao }>,
    ) => {
      const { id, ok, reply, error } = event.data;
      const entry = this.pending.get(id);
      if (!entry) return;
      this.pending.delete(id);
      if (ok && reply) entry.resolve(reply);
      // A recusa atravessou como chave e valores, e volta a ser um erro com
      // chave deste lado: quem chamou trata tudo por um caminho só.
      else if (error) entry.reject(new ErroComChave(error.key, error.params));
      else entry.reject(new ErroComChave("sessao.falhaInesperada", { detalhe: "" }));
    };
    this.worker.onerror = (event) => {
      this.failAll(
        event.message
          ? new ErroComChave("sessao.falhaInesperada", { detalhe: event.message })
          : new ErroComChave("sessao.motorParou"),
      );
    };
  }

  async send(command: Command): Promise<Reply> {
    // Sem esta guarda, uma chamada a um worker já encerrado ficaria pendente
    // para sempre e a tela travaria sem dizer nada. Falhar alto é melhor.
    if (this.disposed) throw encerrada();

    // A restauração precisa acontecer antes do primeiro comando, porque o
    // primeiro comando que a tela manda é um `listSaves`. Restaurar depois
    // mostraria a lista vazia para quem acabou de perder a pasta da WebView.
    await this.espelho.preparar();

    // O `await` acima abre uma janela para a sessão ser encerrada no meio.
    if (this.disposed) throw encerrada();

    this.seq += 1;
    const id = this.seq;
    const reply = await new Promise<Reply>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker.postMessage({ id, command });
    });

    // Sem `await` de propósito: a cópia carrega os megabytes do mundo, e a tela
    // não tem por que esperar por ela para desenhar a rodada que já terminou.
    if (command.type === "deleteSave") void this.espelho.aoApagar(command.saveId);
    else if (GRAVAM.has(command.type)) void this.espelho.aoGravar();

    return reply;
  }

  private failAll(error: Error): void {
    for (const entry of this.pending.values()) entry.reject(error);
    this.pending.clear();
  }

  dispose(): void {
    this.disposed = true;
    this.worker.terminate();
    // O espelho fecha depois do que já estiver na fila dele, não na hora: uma
    // cópia interrompida no meio deixaria o SQLite com um save pela metade.
    void this.espelho.encerrar();
    this.failAll(encerrada());
  }
}

let singleton: GameRunner | null = null;

/**
 * O motor vive enquanto a aba viver.
 *
 * Em desenvolvimento o React monta, desmonta e remonta a árvore para expor
 * efeitos mal escritos. Encerrar o worker na desmontagem e reaproveitar a
 * referência no remonte deixava a sessão falando com um worker morto, e a tela
 * ficava presa em "Montando o mundo" sem erro nenhum. Quem fecha o worker é o
 * navegador, ao fechar a aba.
 */
export function getRunner(): GameRunner {
  if (!singleton) singleton = new GameRunner();
  return singleton;
}

/** Só para testes: descarta o worker atual. */
export function resetRunner(): void {
  singleton?.dispose();
  singleton = null;
}
